<template>
    <div class="valores-espera-container">
      <!-- Alerts Section (Grouped & Collapsible) -->
      <div v-if="alertsLoading" class="loading-indicator-gcn">Cargando alertas...</div>
      <div v-if="alertsError" class="error-message-gcn">{{ alertsError }}</div>
  
      <div v-if="Object.keys(groupedAlerts).length > 0" class="alerts-section">
          <div class="alerts-header">
              <h3>Alertas Pendientes</h3>
          </div>
  
          <!-- Outer loop for alert groups -->
          <div v-for="(groupAlerts, typeName) in groupedAlerts" :key="typeName" class="alert-group" :class="[getAlertTypeClass(groupAlerts[0].type), { 'is-collapsed': alertGroupCollapsedState[typeName] }]">
              <h4 @click="toggleAlertGroup(typeName)" class="collapsible-header">
                  <div class="header-content">
                    <span class="collapse-icon">{{ alertGroupCollapsedState[typeName] ? '▶' : '▼' }}</span>
                    <span class="alert-group-icon material-icons-outlined">notifications</span>
                    {{ typeName }} ({{ groupAlerts.length }})
                  </div>
                  <div class="bulk-actions alert-group-bulk-actions" @click.stop>
                      <select v-model="alertGroupBulkActions[typeName]" :disabled="getSelectedAlertsInGroup(groupAlerts).length === 0">
                          <option value="" disabled>Acciones</option>
                          <option value="ignore">Ignorar Sel.</option>
                          <option v-if="groupAlerts.length > 0 && groupAlerts[0].can_it_be_solved_automatically" value="apply_solution">Aplicar Sol. Sel.</option>
                      </select>
                      <button class="btnGcn btnGcn-secondary btnGcn-sm" @click="applyBulkAlertActionForGroup(groupAlerts, typeName)" :disabled="getSelectedAlertsInGroup(groupAlerts).length === 0 || !alertGroupBulkActions[typeName]">
                          Aplicar
                      </button>
                  </div>
              </h4>
              <div v-show="!alertGroupCollapsedState[typeName]" class="alert-group-content">
                  <table class="alert-table">
                      <thead>
                          <tr>
                              <th><input type="checkbox" :checked="areAllAlertsInGroupSelected(groupAlerts)" @change="toggleSelectAllAlertsInGroup($event, groupAlerts)" title="Seleccionar/Deseleccionar todo en este grupo"></th>
                              <th>Ticker</th>
                              <th>Detalles</th>
                              <th style="width: 220px;">Acciones</th>
                          </tr>
                      </thead>
                      <tbody>
                          <tr v-for="alert in groupAlerts" :key="alert.id" class="alert-item">
                              <td><input type="checkbox" :value="alert.id" v-model="selectedAlerts"></td>
                              <td>{{ alert.ticker }}</td>
                              <td>{{ alert.data.message }}</td>
                              <td class="alert-actions">
                                  <!-- Report error is now only an individual action -->
                                  <button v-if="alert.can_it_report_error && !alert.error_reported" class="btnGcn btnGcn-sm btnGcn-warning" @click="reportAlertError(alert)" title="Reportar Error">Reportar</button>
                                  <button v-if="alert.can_it_be_solved_automatically" class="btnGcn btnGcn-sm btnGcn-success" @click="applyAlertSolution(alert)" title="Aplicar Cambios">Aplicar</button>
                                  <button class="btnGcn btnGcn-sm btnGcn-secondary" @click="ignoreAlert(alert)" title="Ignorar Alerta">Ignorar</button>
                              </td>
                          </tr>
                      </tbody>
                  </table>
              </div>
          </div>
           <p v-if="bulkAlertError" class="error-message-gcn small" style="margin-top: 10px;">{{ bulkAlertError }}</p>
      </div>
  
      <!-- List Actions for Waiting Positions -->
      <div class="list-actions">
         <button class="btnGcn custom-btnGcn" @click="openAddModal">
             <span class="material-icons-outlined">add</span> Añadir Nuevo Valor en Espera
         </button>
         <div class="list-actions-right">
             <!-- Column Customizer -->
             <TableColumnCustomizer 
                 :columns="defaultColumns" 
                 storage-key="valoresEnEspera"
                 @columns-changed="handleColumnsChanged"
             />
             <!-- Separate Bulk Actions for Positions -->
             <div class="bulk-actions position-bulk-actions">
                 <select v-model="selectedBulkPositionAction" :disabled="selectedPositions.length === 0">
                     <option value="" disabled>Acciones múltiples (Posiciones)</option>
                     <option value="delete">Eliminar Seleccionadas</option>
                 </select>
                 <button class="btnGcn btnGcn-secondary btnGcn-sm" @click="applyBulkPositionAction" :disabled="selectedPositions.length === 0 || !selectedBulkPositionAction">Aplicar a Posiciones</button>
             </div>
         </div>
      </div>
  
      <!-- Loading/Error for Waiting List -->
      <div v-if="loading" class="loading-indicator-gcn">Cargando valores en espera...</div>
      <div v-if="error" class="error-message-gcn">{{ error }}</div>
  
      <!-- Waiting Positions Table -->
      <div class="table-responsive" v-if="!loading && !error && waitingPositions.length > 0">
           <table class="table data-table waiting-table">
              <thead>
                  <tr>
                      <th><input type="checkbox" @change="toggleSelectAllPositions" :checked="allPositionsSelected"></th>
                      <th 
                          v-for="column in visibleColumns" 
                          :key="column.key"
                          :class="{ 'sortable-header': column.sortable !== false, 'number-cell': column.numeric }"
                          @click="column.sortable !== false ? sortByColumn(column.key) : null"
                      >
                        {{ column.label }}
                        <span v-if="column.sortable !== false" :class="getSortIconClass(column.key)"></span>
                      </th>
                      <th>Opciones</th>
                  </tr>
              </thead>
               <tbody>
                  <tr v-for="pos in sortedWaitingPositions" :key="pos.id">
                       <td><input type="checkbox" :value="pos.id" v-model="selectedPositions"></td>
                       <td 
                           v-for="column in visibleColumns" 
                           :key="column.key"
                           :class="getCellClass(column)"
                       >
                           <!-- Ticker Cell Special Rendering -->
                           <template v-if="column.key === 'ticker'">
                               <div class="ticker-content-wrapper" :title="generateTickerTooltip(pos)">
                                   <div class="ticker-left">
                                       <b>{{ pos.ticker }}</b>
                                       <small>{{ pos.company_name }}</small>
                                   </div>
                                   <div class="ticker-right">
                                       <span class="direction-icon material-icons-outlined" 
                                             :class="pos.direction == 1 ? 'bullish' : 'bearish'" 
                                             :title="pos.direction == 1 ? 'Alcista' : 'Bajista'">
                                           {{ pos.direction == 1 ? 'trending_up' : 'trending_down' }}
                                       </span>
                                       <span class="instrument-icon material-icons-outlined" 
                                             :title="pos.instrument_type === 'STOCK' ? 'Acción' : 'CFD'">
                                           {{ pos.instrument_type === 'STOCK' ? 'show_chart' : 'candlestick_chart' }}
                                       </span>
                                       <small class="currency">{{ pos.currency || '-' }}</small>
                                   </div>
                               </div>
                           </template>
                           <!-- Out Price Cell Special Rendering -->
                           <template v-else-if="column.key === 'out_price'">
                               <div>
                                   {{ formatPrice(pos.out_price, pos.stock_decimals) }}
                               </div>
                           </template>
                           <!-- Notes Cell Special Rendering -->
                           <template v-else-if="column.key === 'notes'">
                               <span v-if="!pos.editingNotes">{{ pos.notes || '-' }}</span>
                               <textarea v-else class="form-control-gcn form-control-gcn-sm" 
                                         :data-id="pos.id" 
                                         v-model="pos.editNotesValue"></textarea>
                               <button v-if="!pos.editingNotes" class="btnGcn btnGcn-link btnGcn-sm" 
                                       @click="startEditNotes(pos)" title="Editar Notas">
                                   <span class="material-icons-outlined">edit</span>
                               </button>
                               <button v-else class="btnGcn btnGcn-link btnGcn-sm" 
                                       @click="saveNotes(pos)" title="Guardar Notas">
                                   <span class="material-icons-outlined">save</span>
                               </button>
                           </template>
                           <!-- Default Cell Rendering -->
                           <template v-else>
                               <span v-html="renderCell(pos, column)"></span>
                           </template>
                       </td>
                      <td class="options-cell">
                          <!-- Chart Button (Dynamic Chart) -->
                          <button class="btnGcn btnGcn-link btnGcn-sm btnGcn-graph" @click="openDynamicChart(pos.ticker)" title="Graficador Dinámico">
                              <span class="material-icons-outlined">bar_chart</span>
                          </button>
                          <!-- Forum Link (always visible, disabled if URL missing) -->
                          <a v-if="pos.options && pos.options.forum && pos.options.forum.url" :href="pos.options.forum.url" target="_blank" class="btnGcn btnGcn-link btnGcn-sm" :title="pos.options.forum.title || 'Ver foro'">
                              <span class="material-icons-outlined">forum</span>
                          </a>
                          <span v-else class="btnGcn btnGcn-link btnGcn-sm disabled" title="Foro no disponible">
                              <span class="material-icons-outlined">forum</span>
                          </span>
                          <!-- Execute Button -->
                          <button class="btnGcn btnGcn-link btnGcn-sm btnGcn-success" @click="openExecuteModal(pos)" title="Ejecutar Entrada">
                              <span class="material-icons-outlined">bolt</span>
                          </button>
                          <!-- Edit Button -->
                          <button class="btnGcn btnGcn-link btnGcn-sm" @click="openEditModal(pos)" title="Editar">
                              <span class="material-icons-outlined">edit</span>
                          </button>
                          <!-- Delete Button -->
                          <button class="btnGcn btnGcn-link btnGcn-sm btnGcn-danger" @click="confirmDelete(pos.id)" title="Eliminar">
                              <span class="material-icons-outlined">delete</span>
                          </button>
                      </td>
                  </tr>
              </tbody>
          </table>
      </div>
       <div v-else-if="!loading && !error && waitingPositions.length === 0">
          <p>No hay valores en espera para esta cartera.</p>
      </div>
  
      <!-- Add Modal -->
       <Modal :show="showAddModal" title="Añadir Valor en Espera" @close="closeAddModal">
          <template #body> <WaitingPositionForm ref="addForm" :portfolio-id="portfolioId" :is-submitting="isSubmitting" :error="formError" @submit="handleAddWaitingPosition" @error="setFormError" /> </template>
          <template #footer> <button class="btnGcn custom-btnGcn-outline" @click="closeAddModal">Cancelar</button> <button class="btnGcn custom-btnGcn" @click="$refs.addForm.submitForm()" :disabled="isSubmitting"> {{ isSubmitting ? 'Añadiendo...' : 'Añadir Valor' }} </button> </template>
      </Modal>
  
       <!-- Execute Modal -->
      <Modal :show="showExecuteModal" title="Ejecutar Entrada" @close="closeExecuteModal">
          <template #body>
              <div v-if="positionToExecute">
                  <p>Ejecutando entrada para: <strong>{{ positionToExecute.ticker }}</strong></p>
                  <div v-if="executeFormError" class="error-message-gcn">{{ executeFormError }}</div>
                  <form @submit.prevent="handleExecutePosition" ref="executeFormElement">
                      <div class="form-group-gcn">
                          <label for="exec-in-price">Precio Entrada Real</label>
                          <input type="text" required class="form-control-gcn" v-model="executeFormData.in_price" placeholder="Ingrese precio de entrada">
                      </div>
                                             <div class="form-group-gcn">
                          <label for="exec-in-currency">Valor Divisa ({{ portfolioCurrency }} / {{ positionToExecute.currency }})</label>
                          <input 
                              type="text" 
                              required 
                              class="form-control-gcn" 
                              v-model="executeFormData.in_currency"
                              placeholder="Ingrese tasa de cambio"
                              :disabled="exchangeRateLoading">
                          <button 
                              type="button"
                              class="btnGcn btnGcn-secondary btnGcn-sm exchange-rate-btn"
                              @click="fetchExchangeRateManually"
                              :disabled="exchangeRateLoading || !positionToExecute">
                              <span v-if="exchangeRateLoading">🔄 Obteniendo...</span>
                              <span v-else>Obtener automáticamente tasa de cambio para hoy</span>
                          </button>
                          <small v-if="executeFormData.currency_pair && !exchangeRateLoading" class="exchange-rate-success">
                              ✅ Última tasa obtenida para {{ executeFormData.currency_pair }}
                          </small>
                          <small v-else-if="!exchangeRateLoading">
                              Ingrese la tasa de cambio manualmente o use el botón para obtener la tasa actual.
                          </small>
                       </div>
                      <div class="form-group-gcn">
                          <label for="exec-in-date">Fecha Entrada</label>
                          <input type="date" required class="form-control-gcn" :value="formattedExecuteDateForInput" @input="updateExecuteDate">
                          <small>Formato API: d-m-Y ({{ executeFormData.in_date }})</small>
                      </div>
                      <div class="form-group-gcn">
                          <label for="exec-open-fee">Comisión Apertura ({{ portfolioCurrency }})</label>
                          <input type="text" required class="form-control-gcn" v-model="executeFormData.open_fee" placeholder="Ingrese comisión (ej: 5.50)">
                      </div>
                      <input type="hidden" v-model="executeFormData.waiting_id">
                      <button type="submit" style="display: none;"></button>
                  </form>
              </div>
          </template>
          <template #footer>
              <button class="btnGcn custom-btnGcn-outline" @click="closeExecuteModal">Cancelar</button>
              <button class="btnGcn custom-btnGcn" @click="submitExecuteForm" :disabled="isSubmitting">
                  {{ isSubmitting ? 'Ejecutando...' : 'Confirmar Ejecución' }}
              </button>
          </template>
      </Modal>
  
      <!-- Edit Modal -->
      <Modal :show="showEditModal" title="Editar Valor en Espera" @close="closeEditModal">
          <template #body>
              <div v-if="positionToEdit">
                  <p>Editando: <strong>{{ positionToEdit.ticker }}</strong></p>
                  <div v-if="editFormError" class="error-message-gcn">{{ editFormError }}</div>
                  <form @submit.prevent="handleUpdateWaitingPosition" ref="editFormElement">

                      <!-- Mode Selection -->
                      <div class="edit-mode-selector">
                          <h4 class="edit-mode-title">¿Cómo calcular precio de salida y el número de acciones?</h4>
                          
                          <div class="edit-mode-options">
                              <!-- Automatic Mode -->
                              <label class="edit-mode-option" :class="{ 'active': followEurekersMethodology }">
                                  <input type="radio" :value="true" v-model="followEurekersMethodology" name="editMode" />
                                  <div class="mode-content">
                                      <div class="mode-text">
                                          <strong>Automático</strong>
                                          <small>Eurekers los calcula</small>
                                      </div>
                                  </div>
                              </label>
                              
                              <!-- Manual Mode -->
                              <label class="edit-mode-option" :class="{ 'active': !followEurekersMethodology }">
                                  <input type="radio" :value="false" v-model="followEurekersMethodology" name="editMode" />
                                  <div class="mode-content">
                                      <div class="mode-text">
                                          <strong>Manual</strong>
                                          <small>Yo defino todos los campos</small>
                                      </div>
                                  </div>
                              </label>
                          </div>
                      </div>

                      <!-- Entry Price -->
                      <div class="form-group-gcn">
                          <label :for="'edit-in_price-' + positionToEdit.id" class="text-primary">
                              Precio de Entrada <span class="required">*</span>
                          </label>
                          <input 
                              type="text" 
                              :id="'edit-in_price-' + positionToEdit.id" 
                              class="form-control-gcn" 
                              :class="{ 'error': shouldShowEditError('in_price') }"
                              :value="formatEditDisplayValue(editFormData.in_price)"
                              @input="event => handleEditNumericInput(event, 'in_price')"
                              @blur="event => { markEditFieldTouched('in_price'); formatEditNumericOnBlur(event, 'in_price'); }"
                              @focus="event => { markEditFieldTouched('in_price'); handleEditFocus(event, 'in_price'); }"
                              required 
                              :placeholder="priceStep(positionToEdit.stock_decimals).toString()">
                          <small class="text-secondary">Precio por unidad en {{ positionToEdit.currency || 'moneda del instrumento' }}</small>
                          <small v-if="shouldShowEditError('in_price')" class="error-message-gcn">{{ editInPriceError }}</small>
                      </div>
                      

                                             <!-- Support Price -->
                       <div class="form-group-gcn">
                           <label :for="'edit-support_price-' + positionToEdit.id" class="text-primary">
                               Precio de Soporte <span class="required">*</span>
                           </label>
                           <input 
                               type="text" 
                               :id="'edit-support_price-' + positionToEdit.id" 
                               class="form-control-gcn" 
                               :class="{ 'error': shouldShowEditError('support_price') }"
                               :value="formatEditDisplayValue(editFormData.support_price)"
                               @input="event => handleEditNumericInput(event, 'support_price')"
                               @blur="event => { markEditFieldTouched('support_price'); formatEditNumericOnBlur(event, 'support_price'); }"
                               @focus="event => { markEditFieldTouched('support_price'); handleEditFocus(event, 'support_price'); }"
                               required 
                               :placeholder="priceStep(positionToEdit.stock_decimals).toString()">
                           <small class="text-secondary">Precio de soporte técnico en {{ positionToEdit.currency || 'moneda del instrumento' }}</small>
                           <small v-if="shouldShowEditError('support_price')" class="error-message-gcn">{{ editSupportPriceError }}</small>
                       </div>
                       
                       <!-- Support Date -->
                       <div class="form-group-gcn">
                           <label :for="'edit-support_date-' + positionToEdit.id" class="text-primary">
                               Fecha de Soporte <span class="required">*</span>
                           </label>
                           <input 
                               type="date" 
                               :id="'edit-support_date-' + positionToEdit.id" 
                               class="form-control-gcn" 
                               :class="{ 'error': shouldShowEditError('support_date') }"
                               :value="formattedEditSupportDateForInput" 
                               @input="updateEditSupportDate"
                               @blur="markEditFieldTouched('support_date'); validateEditSupportDate()"
                               @focus="markEditFieldTouched('support_date')"
                               required>
                           <small class="text-secondary">Fecha en que se identificó el soporte (Formato API: {{ editFormData.support_date || 'd-m-Y' }})</small>
                           <small v-if="shouldShowEditError('support_date')" class="error-message-gcn">{{ editSupportDateError }}</small>
                       </div>

                      <!-- Additional fields shown only in manual mode -->
                      <div v-if="editManualMode">
                          <!-- Instrument Number -->
                          <div class="form-group-gcn">
                              <label :for="'edit-instrument_number-' + positionToEdit.id" class="text-primary">
                                  Número de Acciones
                              </label>
                              <input 
                                  type="text" 
                                  :id="'edit-instrument_number-' + positionToEdit.id" 
                                  class="form-control-gcn" 
                                  :value="formatEditDisplayValue(editFormData.instrument_number)"
                                  @input="event => handleEditNumericInput(event, 'instrument_number')"
                              >
                          </div>

                          <!-- Out Price -->
                          <div class="form-group-gcn">
                              <label :for="'edit-out_price-' + positionToEdit.id" class="text-primary">
                                  Precio de Salida
                              </label>
                              <input 
                                  type="text" 
                                  :id="'edit-out_price-' + positionToEdit.id" 
                                  class="form-control-gcn" 
                                  :value="formatEditDisplayValue(editFormData.out_price)"
                                  @input="event => handleEditNumericInput(event, 'out_price')"
                              >
                          </div>



                      </div>
                      <button type="submit" style="display: none;"></button>
                  </form>
              </div>
          </template>
          <template #footer>
              <button class="btnGcn custom-btnGcn-outline" @click="closeEditModal">Cancelar</button>
              <button class="btnGcn custom-btnGcn" @click="submitEditForm" :disabled="isSubmitting">
                  {{ isSubmitting ? 'Guardando...' : 'Guardar Cambios' }}
              </button>
          </template>
      </Modal>
  
      <!-- Generic Confirmation Modal -->
      <Modal :show="showConfirmModal" :title="confirmModalTitle" @close="closeConfirmModal" size="sm">
          <template #body>
              <p>{{ confirmModalMessage }}</p>
              <!-- Error display within the confirmation modal -->
              <div v-if="formError && !executeFormError && !editFormError && !bulkAlertError" class="error-message-gcn">{{ formError }}</div>
          </template>
          <template #footer>
              <button class="btnGcn custom-btnGcn-outline" @click="closeConfirmModal" :disabled="isConfirmSubmitting">Cancelar</button>
              <button class="btnGcn custom-btnGcn" @click="executeConfirmAction" :disabled="isConfirmSubmitting">
                  {{ isConfirmSubmitting ? 'Confirmando...' : 'Confirmar' }}
              </button>
          </template>
      </Modal>
  
      <!-- Report Error Modal -->
      <Modal :show="showReportErrorModal" title="Reportar Error en Alerta" @close="closeReportErrorModal">
          <template #body>
              <div v-if="alertToReportError">
                  <p>Reportando error para alerta en ticker: <strong>{{ alertToReportError.ticker }}</strong></p>
                  <p><small>{{ alertToReportError.data.message }}</small></p>
                  <div v-if="reportErrorFormError" class="error-message-gcn">{{ reportErrorFormError }}</div>
                  <div class="form-group-gcn">
                      <label for="report-error-message-gcn">Mensaje adicional (opcional):</label>
                      <textarea id="report-error-message-gcn" class="form-control-gcn" v-model="reportErrorMessage" rows="3"></textarea>
                  </div>
              </div>
          </template>
          <template #footer>
              <button class="btnGcn custom-btnGcn-outline" @click="closeReportErrorModal" :disabled="reportErrorSubmitting">Cancelar</button>
              <button class="btnGcn custom-btnGcn" @click="handleReportErrorConfirmed" :disabled="reportErrorSubmitting">
                  {{ reportErrorSubmitting ? 'Reportando...' : 'Confirmar Reporte' }}
              </button>
          </template>
      </Modal>
  
    </div>
  </template>
  
  <script>
  import apiService, { formatDateDMY, parseDate, formatCurrency } from './apiService';
  import Modal from './Modal.vue';
  import WaitingPositionForm from './WaitingPositionForm.vue';
  import APICaller from "@/components/codeComponents/APICaller.js";
  import TableColumnCustomizer from '@/components/TableColumnCustomizer.vue';
  
  export default {
    name: 'ValoresEnEspera',
    components: {
      Modal,
      WaitingPositionForm,
      TableColumnCustomizer,
    },
    mixins: [APICaller],
    props: {
      portfolioId: { type: String, required: true, },
    },
    data() {
      return {
        loading: false, error: null, waitingPositions: [], portfolioCurrency: 'EUR',
        alertsLoading: false, alertsError: null, alerts: [],
        showAddModal: false, showExecuteModal: false, showEditModal: false,
        formError: null, executeFormError: null, editFormError: null,
        isSubmitting: false,
        positionToExecute: null,
        executeFormData: { waiting_id: '', in_price: '', in_currency: '', currency_pair: '', in_date: formatDateDMY(new Date()), open_fee: '0', },
        positionToEdit: null,
        editFormData: { 
            in_price: null, 
            support_price: null, 
            support_date: '', 
            instrument_number: null, 
            out_price: null
        },

        // Edit Form Validation
        editInPriceError: null,
        editSupportPriceError: null,
        editSupportDateError: null,
        editInPriceValid: false,
        editSupportPriceValid: false,
        editSupportDateValid: false,
        editTouchedFields: {
            in_price: false,
            support_price: false,
            support_date: false
        },
        editSubmitAttempted: false,
        // Separate Bulk Actions state
        selectedPositions: [], selectedBulkPositionAction: '',
        selectedAlerts: [], selectedBulkAlertAction: '', bulkAlertError: null,
        // Alert Group Bulk Actions - separate state per group
        alertGroupBulkActions: {},
        // Alert Group Collapse State
        alertGroupCollapsedState: {},

        // Generic Confirmation Modal State (similar to CarterasList.vue)
        showConfirmModal: false,
        confirmModalTitle: '',
        confirmModalMessage: '',
        confirmModalAction: null,
        confirmModalData: null,
        isConfirmSubmitting: false,

        // State for Report Error Modal
        showReportErrorModal: false,
        alertToReportError: null,
        reportErrorMessage: '',
        reportErrorSubmitting: false,
        reportErrorFormError: null,

        // Estado para controlar la carga de tasa de cambio
        exchangeRateLoading: false,

        // Sorting state
        sortBy: 'ticker',
        sortDirection: 'asc', // 'asc' or 'desc'
        // Enables manual editing of all fields (disables backend auto-calculation)
        editManualMode: false,

        // Column customization
        activeColumns: [],
        defaultColumns: [
          { key: 'ticker', label: 'Ticker', visible: true, mandatory: true, numeric: false },
          { key: 'risk', label: 'Riesgo', visible: true, mandatory: true, numeric: true },
          { key: 'instrument_number', label: 'Acciones', visible: true, mandatory: true, numeric: true },
          { key: 'in_price', label: 'P. Entrada', visible: true, mandatory: true, numeric: true },
          { key: 'out_price', label: 'P. Salida', visible: true, mandatory: true, numeric: true },
          { key: 'support_price', label: 'P. Soporte', visible: true, mandatory: true, numeric: true },
          { key: 'support_date', label: 'Fecha Soporte', visible: true, mandatory: true, numeric: false },
          { key: 'current_price', label: 'P. Actual', visible: true, mandatory: false, numeric: true },
          { key: 'amount', label: 'Montante', visible: true, mandatory: false, numeric: true },
          //{ key: 'currency_value', label: 'Valor Divisa', visible: true, mandatory: false, numeric: true },
          { key: 'tthh_profit', label: 'Rentab. LT', visible: true, mandatory: false, numeric: true },
          { key: 'entry_distance', label: 'Dist. Entrada', visible: true, mandatory: false, numeric: true },
          { key: 'wave_number', label: 'Nº Onda', visible: true, mandatory: false, numeric: true },
          { key: 'notes', label: 'Notas', visible: true, mandatory: false, numeric: false, sortable: false },
          // { key: 'cfd_margin', label: 'Margen CFD', visible: true, mandatory: false, numeric: true },
          // { key: 'is_ipo', label: 'IPO', visible: true, mandatory: false, numeric: false },
        ],
      };
    },
     computed: {
        allPositionsSelected() { return this.waitingPositions.length > 0 && this.selectedPositions.length === this.waitingPositions.length; },
        formattedExecuteDateForInput() { if (!this.executeFormData.in_date) return ''; const date = parseDate(this.executeFormData.in_date); if (!date) return ''; return date.toISOString().split('T')[0]; },
        formattedEditSupportDateForInput() { if (!this.editFormData.support_date) return ''; const date = parseDate(this.editFormData.support_date); if (!date) return ''; return date.toISOString().split('T')[0]; },
        groupedAlerts() {
          if (!this.alerts || this.alerts.length === 0) { return {}; }
          // Filter alerts to only show those for waiting positions (not open positions)
          const waitingAlerts = this.alerts.filter(alert => alert.instrument._type === 'Waiting');
          return waitingAlerts.reduce((groups, alert) => { const groupName = alert.type_name || 'Alertas Generales'; if (!groups[groupName]) { groups[groupName] = []; } groups[groupName].push(alert); return groups; }, {});
       },

       sortedWaitingPositions() {
         if (!this.waitingPositions || this.waitingPositions.length === 0) {
           return [];
         }
         
         const sorted = [...this.waitingPositions].sort((a, b) => {
           let aValue = this.getSortValue(a, this.sortBy);
           let bValue = this.getSortValue(b, this.sortBy);
           
           // Handle null/undefined values
           if (aValue === null || aValue === undefined) aValue = '';
           if (bValue === null || bValue === undefined) bValue = '';
           
           // Convert to strings for comparison if needed
           if (typeof aValue === 'string') aValue = aValue.toLowerCase();
           if (typeof bValue === 'string') bValue = bValue.toLowerCase();
           
           let comparison = 0;
           if (aValue < bValue) comparison = -1;
           else if (aValue > bValue) comparison = 1;
           
           return this.sortDirection === 'desc' ? -comparison : comparison;
         });
         
         return sorted;
       },
       isEditFormValid() {
           return this.editInPriceValid && 
                  this.editSupportPriceValid && this.editSupportDateValid && !this.hasEditErrors;
       },
       hasEditErrors() {
           return !!(this.editInPriceError || 
                    this.editSupportPriceError || this.editSupportDateError);
       },
       followEurekersMethodology: {
           get() {
               return !this.editManualMode;
           },
           set(value) {
               this.editManualMode = !value;
           }
       },
       visibleColumns() {
           return this.activeColumns.filter(column => column.visible);
       },
     },
    methods: {
      // --- European Number Formatting Helpers ---
      formatNumberAnglo(value, decimals = 2) {
        // Handle null, undefined, empty string specifically
        if (value === null || value === undefined || value === '') {
          return 'N/A';
        }
        const num = typeof value === 'number' ? value : Number(value);
        if (isNaN(num)) return 'N/A';
        const absValue = Math.abs(num);
        const parts = absValue.toFixed(decimals).split('.');
        const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        const decimalPart = parts[1];
        return decimalPart ? `${integerPart}.${decimalPart}` : integerPart;
      },
      formatCurrencyAnglo(value, currency = 'EUR') {
        // Handle null, undefined, empty string, or 0
        if (value === null || value === undefined || value === '' || value === 0) {
          return '0.00€';
        }
        const num = typeof value === 'number' ? value : Number(value);
        if (isNaN(num)) return 'N/A';
        const isNegative = num < 0;
        const formattedNumber = this.formatNumberAnglo(Math.abs(num));
        const currencySymbol = currency === 'EUR' ? '€' : currency;
        return isNegative ? `-${formattedNumber}${currencySymbol}` : `${formattedNumber}${currencySymbol}`;
      },
      // --- Currency Symbol Mapper ---
      getCurrencySymbol(currency) {
        const symbols = {
          'EUR': '€',
          'USD': '$',
          'GBP': '£',
          'JPY': '¥',
          'CNY': '¥',
          'CHF': 'CHF',
          'CAD': 'C$',
          'AUD': 'A$',
          'NZD': 'NZ$',
          'HKD': 'HK$',
          'SGD': 'S$',
          'SEK': 'kr',
          'NOK': 'kr',
          'DKK': 'kr',
          'PLN': 'zł',
          'CZK': 'Kč',
          'HUF': 'Ft',
          'RUB': '₽',
          'INR': '₹',
          'KRW': '₩',
          'TRY': '₺',
          'BRL': 'R$',
          'MXN': 'MX$',
          'CLP': 'CL$',
          'COP': 'CO$',
          'ARS': 'AR$',
          'PEN': 'S/',
          'ZAR': 'R',
          'THB': '฿',
          'MYR': 'RM',
          'IDR': 'Rp',
          'PHP': '₱',
          'VND': '₫',
          'TWD': 'NT$',
          'ILS': '₪',
          'AED': 'د.إ',
          'SAR': '﷼',
          'QAR': 'ر.ق',
          'KWD': 'د.ك',
          'BHD': 'د.ب',
          'OMR': 'ر.ع.',
          'JOD': 'د.أ',
          'LBP': 'ل.ل',
          'EGP': 'ج.م'
        };
        return symbols[currency] || currency;
      },
      // --- Formatters ---
      formatCurrency(value, currency = 'EUR') {
        return this.formatCurrencyAnglo(value, currency);
      },
      formatDate(d) { const dt=parseDate(d); return dt?dt.toLocaleDateString('es-ES'):(d||''); },
      formatPercentage(v){ const num = typeof v === 'number' ? v : Number(v); if (isNaN(num)) return 'N/A'; return `${this.formatNumberAnglo(num)}%`; },
      formatPrice(v,d){ const num = typeof v === 'number' ? v : Number(v); if(isNaN(num))return 'N/A'; const nd=(typeof d==='number'&&d>=0)?d:2; return this.formatNumberAnglo(num, nd);},
      formatNumber(v) { const num = typeof v === 'number' ? v : Number(v); return isNaN(num) ? 'N/A' : this.formatNumberAnglo(num, 0); },
  
      // --- Tooltip Generator ---
      generateTickerTooltip(pos) {
        const entryType = `Tipo Entrada: ${pos.entry_type_name || 'N/A'}`;
        const index = `Índice: ${pos.valor_tthh_index_valor || '-'}`;
        const sector = `Sector: ${pos.valor_tthh_sector || '-'}`;
        const dividend = `Dividendo: ${pos.valor_tthh_avg_dividends !== undefined ? pos.valor_tthh_avg_dividends : '-'}`;
        return `${entryType}\n${index}\n${sector}\n${dividend}`;
      },
  
      // --- Data Fetching ---
      async fetchData() { if (!this.portfolioId) return; this.fetchWaitingPositions(); this.fetchAlerts(); },
      async fetchWaitingPositions() {
          this.loading = true; this.error = null; this.selectedPositions = [];
          try {
              const positions = await apiService.listWaitingPositions(this.portfolioId);
              this.waitingPositions = positions.map(p => ({ ...p, editingNotes: false, editNotesValue: p.notes }));
              if (positions.length > 0 && positions[0].portfolio_currency) {
                this.portfolioCurrency = positions[0].portfolio_currency;
              }
          } catch (err) { this.error = `Error cargando valores en espera: ${err.message}`; this.waitingPositions = []; } finally { this.loading = false; }
      },
       async fetchAlerts() {
          this.alertsLoading = true; this.alertsError = null; this.bulkAlertError = null; this.selectedAlerts = [];
          try {
              const response = await apiService.getPortfolioAlerts(this.portfolioId);
              if (response && response.list && Array.isArray(response.list['#items'])) { this.alerts = response.list['#items']; }
              else if (Array.isArray(response)) { this.alerts = response; }
              else { this.alerts = []; console.warn("Unexpected structure for alerts response:", response); }
              this.initializeAlertGroupState();
          } catch (err) { this.alertsError = `Error cargando alertas: ${err.message}`; this.alerts = []; }
           finally { this.alertsLoading = false; }
       },
       initializeAlertGroupState() {
           const newState = {};
           const newBulkActions = {};
           for (const typeName in this.groupedAlerts) {
               newState[typeName] = this.alertGroupCollapsedState[typeName] !== undefined ? this.alertGroupCollapsedState[typeName] : true; // Default collapsed
               newBulkActions[typeName] = this.alertGroupBulkActions[typeName] || ''; // Default empty action
           }
           this.alertGroupCollapsedState = newState;
           this.alertGroupBulkActions = newBulkActions;
       },
  

  
      // --- Modal & Form Handling ---
      openAddModal() { this.formError = null; this.showAddModal = true; },
      closeAddModal() { this.showAddModal = false; },
      setFormError(message) { this.formError = message; },
          openExecuteModal(position) {
        this.executeFormError = null; this.positionToExecute = position;
        this.executeFormData = { 
            waiting_id: position.id, 
            in_price: position.in_price ? position.in_price.toString() : '', 
            in_currency: '', // Empty field to start
            currency_pair: '', 
            in_date: formatDateDMY(new Date()), 
            open_fee: '0', 
        };
        this.showExecuteModal = true;
    },
      closeExecuteModal() { this.showExecuteModal = false; this.positionToExecute = null; },
      openEditModal(position) {
          this.editFormError = null;
          this.positionToEdit = position;
          // Clone relevant data to avoid modifying original object directly via v-model
          this.editFormData = {
              in_price: position.in_price,
              support_price: position.support_price,
              support_date: position.support_date ? formatDateDMY(parseDate(position.support_date)) : '',
          };
          
          // If manual mode is needed, add extra fields
          this.editFormData = {
              in_price: position.in_price,
              support_price: position.support_price,
              support_date: position.support_date ? formatDateDMY(parseDate(position.support_date)) : '',
              instrument_number: position.instrument_number,
              out_price: position.out_price
          };
          this.editManualMode = false;
          
          // Reset validation state
          this.editInPriceError = null;
          this.editSupportPriceError = null;
          this.editSupportDateError = null;
          this.editInPriceValid = false;
          this.editSupportPriceValid = false;
          this.editSupportDateValid = false;
          this.editTouchedFields = {
              in_price: false,
              support_price: false,
              support_date: false
          };
          this.editSubmitAttempted = false;
          
          this.showEditModal = true;
          
          // Validate initial values after modal is shown
          this.$nextTick(() => {
              this.validateEditInitialValues();
          });
      },
      closeEditModal() {
          this.showEditModal = false;
          this.positionToEdit = null;
          this.editFormData = { in_price: null, support_price: null, support_date: '' }; // Reset form
          // Reset validation state
          this.editInPriceError = null;
          this.editSupportPriceError = null;
          this.editSupportDateError = null;
          this.editInPriceValid = false;
          this.editSupportPriceValid = false;
          this.editSupportDateValid = false;
          this.editTouchedFields = {
              in_price: false,
              support_price: false,
              support_date: false
          };
          this.editSubmitAttempted = false;
          this.editFormData = { 
              in_price: null, 
              support_price: null, 
              support_date: '', 
              instrument_number: null, 
              out_price: null
          }; // Reset form
          this.editManualMode = false;
      },
      updateExecuteDate(event) { const d=event.target.value; this.executeFormData.in_date = d?formatDateDMY(new Date(d+'T00:00:00')):''; },
      updateEditSupportDate(event) { 
          const d=event.target.value; 
          this.editFormData.support_date = d?formatDateDMY(new Date(d+'T00:00:00')):''; 
          this.validateEditSupportDate();
      },
      submitExecuteForm() { if (this.$refs.executeFormElement && this.$refs.executeFormElement.checkValidity()) { this.handleExecutePosition(); } else { if (this.$refs.executeFormElement) this.$refs.executeFormElement.reportValidity(); this.executeFormError = "Corrija los errores."; } },
      submitEditForm() {
          this.editSubmitAttempted = true;
          
          // Mark all fields as touched
          this.markEditFieldTouched('in_price');
          this.markEditFieldTouched('support_price');
          this.markEditFieldTouched('support_date');

          // Validate all fields
          this.validateEditInPrice();
          this.validateEditSupportPrice();
          this.validateEditSupportDate();

          if (!this.isEditFormValid || this.hasEditErrors) {
              this.editFormError = "Por favor, corrija los errores del formulario antes de continuar.";
              return;
          }

          this.handleUpdateWaitingPosition();
      },
  
          // --- Exchange Rate Fetching (nuevo método basado en Risk.vue) ---
    async fetchExchangeRateManually() {
        if (!this.positionToExecute || !this.positionToExecute.currency || !this.portfolioCurrency) {
            this.executeFormError = 'No se puede obtener tasa de cambio: faltan datos de divisa';
            return;
        }

        // Si las divisas son iguales, el tipo de cambio es 1
        if (this.positionToExecute.currency === this.portfolioCurrency) {
            this.executeFormData.in_currency = '1';
            this.executeFormData.currency_pair = `${this.portfolioCurrency}/${this.positionToExecute.currency}`;
            return;
        }

        this.exchangeRateLoading = true;
        this.executeFormError = null; // Clear any previous errors
        
        try {
            // Get rate as portfolioCurrency / positionCurrency (inverted from original)
            const rate = await this.getExchangeRate(this.portfolioCurrency, this.positionToExecute.currency);
            this.executeFormData.in_currency = rate.toString();
            this.executeFormData.currency_pair = `${this.portfolioCurrency}/${this.positionToExecute.currency}`;
        } catch (error) {
            console.error('Error obteniendo tasa de cambio:', error);
            this.executeFormError = `Error obteniendo tasa de cambio ${this.portfolioCurrency}/${this.positionToExecute.currency}: ${error.message}`;
        } finally {
            this.exchangeRateLoading = false;
        }
    },

      // --- Método para obtener tasa de cambio via API (basado en Risk.vue) ---
      getExchangeRate(fromCurrency, toCurrency) {
          return new Promise((resolve, reject) => {
              if (fromCurrency === toCurrency) {
                  resolve(1);
                  return;
              }
              
              const url = `/api/v1/calculator-exchanges/${fromCurrency}/${toCurrency}`;
              
              const successCallback = response => {
                  const rate = response?.data?.chart?.result?.[0]?.meta?.chartPreviousClose;
                  if (typeof rate === 'number') {
                      const formattedRate = parseFloat(rate.toFixed(4));
                      resolve(formattedRate);
                  } else {
                      reject(new Error(`Respuesta inesperada para ${fromCurrency}/${toCurrency}`));
                  }
              };
              
              const failureCallback = (error) => {
                  reject(new Error(`No se pudo obtener tasa ${fromCurrency}/${toCurrency}`));
              };

              const successHandler = new APICaller.SuccessHandler(successCallback);
              const failureHandler = new APICaller.FailureHandler('API_ERR_EXCHANGE_RATE', `Error API ${fromCurrency}/${toCurrency}`, failureCallback);
              this._getAPICall(url, successHandler, failureHandler);
          });
      },
  
      // --- API Actions (Individual) ---
      async handleAddWaitingPosition(formData) {
          this.formError = null; this.isSubmitting = true;
          try { await apiService.addWaitingPosition(this.portfolioId, formData); this.closeAddModal(); await this.fetchData(); }
          catch (err) { this.formError = `Error al añadir: ${err.message}`; } finally { this.isSubmitting = false; }
      },
          async handleExecutePosition() {
        this.executeFormError = null; this.isSubmitting = true;
        try { 
            // Convert text inputs to numbers for API
            const dataToSend = {
                ...this.executeFormData,
                in_price: parseFloat(this.executeFormData.in_price),
                in_currency: parseFloat(this.executeFormData.in_currency),
                open_fee: parseFloat(this.executeFormData.open_fee)
            };
            await apiService.executeWaitingPosition(this.portfolioId, dataToSend); 
            this.closeExecuteModal(); 
            await this.fetchData(); 
        }
        catch (err) { this.executeFormError = `Error al ejecutar: ${err.message}`; } finally { this.isSubmitting = false; }
    },
      async handleUpdateWaitingPosition() {
          if (!this.positionToEdit) return;
          this.editFormError = null; this.isSubmitting = true;
          try {
              // Only send fields that are being edited
              let updateData;
              if (this.editManualMode) {
                  updateData = { ...this.editFormData, manual_override: 1 };
              } else {
                  updateData = {
                      in_price: this.editFormData.in_price,
                      support_price: this.editFormData.support_price,
                      support_date: this.editFormData.support_date,
                  };
              }
              await apiService.updateWaitingPosition(this.portfolioId, this.positionToEdit.id, updateData);
              this.closeEditModal();
              await this.fetchData(); // Refresh list to show changes
          } catch (err) {
              this.editFormError = `Error al actualizar: ${err.message}`;
          } finally {
              this.isSubmitting = false;
          }
      },
      async performDeleteWaitingPosition(positionId) {
        // Actual deletion logic
        this.loading = true; // Use main table loading for this for now
        this.error = null;
        try {
            await apiService.deleteWaitingPosition(this.portfolioId, positionId);
            await this.fetchData(); // Refreshes waiting positions and alerts
        } catch (err) {
            // this.error = `Error al eliminar: ${err.message}`;
            // Re-throw to be caught by executeConfirmAction and displayed in the modal
            throw new Error(`Al eliminar valor en espera: ${err.message}`);
        } finally {
            this.loading = false;
        }
      },
      confirmDelete(positionId) {
        const position = this.waitingPositions.find(p => p.id === positionId);
        const tickerName = position ? position.ticker : 'este valor';
        this.openConfirmModal(
            'Confirmar Eliminación',
            `¿Estás seguro de que quieres eliminar ${tickerName} de la lista de valores en espera?`,
            () => this.performDeleteWaitingPosition(positionId), // Pass as an arrow function to maintain context
            null // No extra data needed for this action beyond positionId
        );
      },
      startEditNotes(position) {
           const list = this.waitingPositions.find(p => p.id === position.id) ? this.waitingPositions : []; // Assuming notes only on waiting list
           list.forEach(p => p.editingNotes = false);
           position.editNotesValue = position.notes; position.editingNotes = true;
           this.$nextTick(() => { const ta = this.$refs.notesTextarea; if(ta) { const t=Array.isArray(ta)?ta.find(t=>t.dataset.id===position.id):ta; if(t)t.focus(); } });
      },
      cancelEditNotes(position) { position.editingNotes = false; },
      async saveNotes(position) {
          if (position.notes === position.editNotesValue) { 
              position.editingNotes = false; 
              return; 
          }
          
          const oldNotes = position.notes; // Store old notes for revert
          position.notes = position.editNotesValue; // Optimistic UI update
          position.editingNotes = false; // Close editor optimistically
          this.isSubmitting = true; // Set submitting state, ensure there's a visual cue if needed

          try {
              const updateData = { notes: position.editNotesValue };
              // Assuming API returns the updated position or at least confirms success
              await apiService.updateWaitingPosition(this.portfolioId, position.id, updateData);
              // position.notes is already updated optimistically
              // Add toast success here if available: this.$toast.success('Notas guardadas');
          } catch (err) {
              console.error("Error saving notes:", err);
              position.notes = oldNotes; // Revert on error
              position.editNotesValue = oldNotes; // Also revert the value in edit field if user re-opens
              // position.editingNotes = true; // Optionally, re-open editing on error
              
              // Propagate the error. If this component had a dedicated error display for notes, we'd use it.
              // For now, throwing it. If wrapped in a modal, that modal would catch it.
              this.error = `Error guardando notas para ${position.ticker}: ${err.message}`; // Use general error display for now
              // throw new Error(`Error guardando notas: ${err.message}`); 
          } finally {
              this.isSubmitting = false;
          }
      },
       async reportAlertError(alert) {
           this.openReportErrorModal(alert);
       },
        async performApplyAlertSolution(alert) {
            // Actual logic to apply solution
            this.alertsLoading = true; // Use alerts section loading indicator
            try {
                await apiService.applyAlertSolution(this.portfolioId, alert.id);
                await this.fetchData(); // Refreshes alerts and potentially positions
            } catch (err) {
                throw new Error(`Al aplicar solución para alerta en ${alert.ticker}: ${err.message}`);
            } finally {
                this.alertsLoading = false;
            }
        },
        applyAlertSolution(alert) {
           this.openConfirmModal(
               'Aplicar Solución de Alerta',
               `¿Confirmas que quieres aplicar la solución automática para la alerta en ${alert.ticker} (${alert.data.message})?`,
               () => this.performApplyAlertSolution(alert),
               alert // Pass alert data if needed, though action uses alert from its closure
           );
        },
        async ignoreAlert(alert) {
           this.alertsLoading = true;
            try { await apiService.createAlertState(this.portfolioId, { alert_id: alert.id, state: 'ignore', ticker: alert.ticker }); await this.fetchAlerts(); }
            catch (err) { this.alertsError = `Error ignorando alerta: ${err.message}`; } finally { this.alertsLoading = false; }
        },
  
  
       // --- Bulk Actions (Positions) ---
       toggleSelectAllPositions(event) { this.selectedPositions = event.target.checked ? this.waitingPositions.map(p => p.id) : []; },
       async performBulkDeletePositions() {
            // Actual logic for bulk deleting positions
            this.loading = true; // Main table loading
            this.error = null;
            let failed = 0;
            try {
                for (const id of this.selectedPositions) {
                    try {
                        await apiService.deleteWaitingPosition(this.portfolioId, id);
                    } catch (err) {
                        console.error(`Bulk delete failed for position ID ${id}:`, err);
                        failed++;
                    }
                }
            } catch (err) {
                // This catch might not be strictly necessary if individual errors are handled,
                // but can catch broader issues with the loop or setup.
                // Re-throw to be caught by executeConfirmAction
                throw new Error(`Error durante la eliminación múltiple: ${err.message}`);
            }
            await this.fetchData(); // Refresh list
            if (failed > 0) {
                // Set error that will be displayed in the confirmation modal by executeConfirmAction
                throw new Error(`${failed} eliminaciones de valores en espera fallaron.`);
            }
            // On complete success, modal closes. Message could be a toast.
       },
       applyBulkPositionAction() {
           if (this.selectedPositions.length === 0 || !this.selectedBulkPositionAction) return;
           if (this.selectedBulkPositionAction === 'delete') {
                this.openConfirmModal(
                    'Confirmar Eliminación Múltiple',
                    `¿Estás seguro de que quieres eliminar los ${this.selectedPositions.length} valores en espera seleccionados?`,
                    this.performBulkDeletePositions, // Action to call
                    null // No specific data needed, uses this.selectedPositions
                );
           }
           // Reset the select element after initiating action or if no confirm (handled by closing modal)
           // this.selectedBulkPositionAction = ''; // Can be cleared in closeConfirmModal or after successful action
       },
  
       // --- Alert Group Collapse ---
       toggleAlertGroup(typeName) { this.$set(this.alertGroupCollapsedState, typeName, !this.alertGroupCollapsedState[typeName]); },
  
       // --- Bulk Actions (Alerts) ---
       areAllAlertsInGroupSelected(groupAlerts) { if (!groupAlerts || groupAlerts.length === 0) return false; return groupAlerts.every(alert => this.selectedAlerts.includes(alert.id)); },
       toggleSelectAllAlertsInGroup(event, groupAlerts) {
           const groupAlertIds = groupAlerts.map(alert => alert.id);
           if (event.target.checked) { groupAlertIds.forEach(id => { if (!this.selectedAlerts.includes(id)) { this.selectedAlerts.push(id); } }); }
           else { this.selectedAlerts = this.selectedAlerts.filter(id => !groupAlertIds.includes(id)); }
       },
              async performBulkAlertActionForGroup(selectedAlertIds, action, typeName) {
             // Actual logic for bulk alert actions for a specific group
             this.alertsLoading = true;
             this.bulkAlertError = null; // Use this for specific feedback below the alerts table
             let successes = 0;
             let failures = 0;

             for (const alertId of selectedAlertIds) {
                 const alert = this.alerts.find(a => a.id === alertId);
                 if (!alert) {
                     failures++;
                     continue;
                 }
                 try {
                     if (action === 'ignore') {
                         await apiService.createAlertState(this.portfolioId, { alert_id: alert.id, state: 'ignore', ticker: alert.ticker });
                     } else if (action === 'apply_solution') {
                         if (alert.can_it_be_solved_automatically) {
                             await apiService.applyAlertSolution(this.portfolioId, alert.id);
                         } else {
                             console.warn(`Solución automática no disponible para alerta ${alertId} (${alert.ticker})`);
                             failures++; // Count as failure if solution cannot be applied
                             continue;
                         }
                     }
                     successes++;
                 } catch (err) {
                     console.error(`Error durante acción múltiple '${action}' en alerta ${alertId} (${alert.ticker}):`, err);
                     failures++;
                 }
             }
             await this.fetchData(); // Refresh alerts and potentially positions

             if (failures > 0) {
                 this.bulkAlertError = `Acción '${action}' completada para ${successes} alertas. ${failures} fallaron (ver consola para detalles).`;
                 // Do not throw error here to keep it displayed as bulkAlertError, not in modal
             } else {
                 // this.$toast.success(`Acción '${action}' completada para ${successes} alertas.`); // Optional toast for success
                 console.log(`Acción '${action}' completada para ${successes} alertas.`);
             }
             this.alertsLoading = false;
             // Remove processed alerts from global selection
             this.selectedAlerts = this.selectedAlerts.filter(id => !selectedAlertIds.includes(id));
             // Reset only this group's dropdown
             this.$set(this.alertGroupBulkActions, typeName, '');
        },
       async performBulkAlertAction() {
            // Actual logic for bulk alert actions
            this.alertsLoading = true;
            this.bulkAlertError = null; // Use this for specific feedback below the alerts table
            let successes = 0;
            let failures = 0;
            const action = this.selectedBulkAlertAction; // Get action from data property
            const alertIds = [...this.selectedAlerts]; // Use current selection

            for (const alertId of alertIds) {
                const alert = this.alerts.find(a => a.id === alertId);
                if (!alert) {
                    failures++;
                    continue;
                }
                try {
                    if (action === 'ignore') {
                        await apiService.createAlertState(this.portfolioId, { alert_id: alert.id, state: 'ignore', ticker: alert.ticker });
                    } else if (action === 'apply_solution') {
                        if (alert.can_it_be_solved_automatically) {
                            await apiService.applyAlertSolution(this.portfolioId, alert.id);
                        } else {
                            console.warn(`Solución automática no disponible para alerta ${alertId} (${alert.ticker})`);
                            failures++; // Count as failure if solution cannot be applied
                            continue;
                        }
                    }
                    successes++;
                } catch (err) {
                    console.error(`Error durante acción múltiple '${action}' en alerta ${alertId} (${alert.ticker}):`, err);
                    failures++;
                }
            }
            await this.fetchData(); // Refresh alerts and potentially positions

            if (failures > 0) {
                this.bulkAlertError = `Acción '${action}' completada para ${successes} alertas. ${failures} fallaron (ver consola para detalles).`;
                // Do not throw error here to keep it displayed as bulkAlertError, not in modal
            } else {
                // this.$toast.success(`Acción '${action}' completada para ${successes} alertas.`); // Optional toast for success
                console.log(`Acción '${action}' completada para ${successes} alertas.`);
            }
            this.alertsLoading = false;
            this.selectedAlerts = []; // Clear selection after action
            this.selectedBulkAlertAction = ''; // Reset dropdown
       },
       getSelectedAlertsInGroup(groupAlerts) {
           const groupAlertIds = groupAlerts.map(alert => alert.id);
           return this.selectedAlerts.filter(alertId => groupAlertIds.includes(alertId));
       },
       applyBulkAlertActionForGroup(groupAlerts, typeName) {
           const selectedInGroup = this.getSelectedAlertsInGroup(groupAlerts);
           const selectedAction = this.alertGroupBulkActions[typeName];
           if (selectedInGroup.length === 0 || !selectedAction) return;
           
           const action = selectedAction;
           const numSelected = selectedInGroup.length;
           const groupName = groupAlerts.length > 0 ? groupAlerts[0].type_name || 'Alertas Generales' : 'Alertas';
           let confirmationMessage = `¿Estás seguro de que quieres aplicar la acción "${selectedAction === 'ignore' ? 'Ignorar' : 'Aplicar Solución'}" a las ${numSelected} alertas seleccionadas en el grupo "${groupName}"?`;
           if (action === 'apply_solution') {
               confirmationMessage += "\nSe intentará aplicar la solución automática donde sea posible.";
           }

           this.openConfirmModal(
               'Confirmar Acción Múltiple de Alertas',
               confirmationMessage,
               () => this.performBulkAlertActionForGroup(selectedInGroup, selectedAction, typeName), // Action to call
               null // No specific data needed, uses selectedInGroup
           );
       },
       applyBulkAlertAction() {
           if (this.selectedAlerts.length === 0 || !this.selectedBulkAlertAction) return;
           
           const action = this.selectedBulkAlertAction;
           const numSelected = this.selectedAlerts.length;
           let confirmationMessage = `¿Estás seguro de que quieres aplicar la acción "${this.selectedBulkAlertAction === 'ignore' ? 'Ignorar' : 'Aplicar Solución'}" a las ${numSelected} alertas seleccionadas?`;
           if (action === 'apply_solution') {
               confirmationMessage += "\nSe intentará aplicar la solución automática donde sea posible.";
           }

           this.openConfirmModal(
               'Confirmar Acción Múltiple de Alertas',
               confirmationMessage,
               this.performBulkAlertAction, // Action to call
               null // No specific data needed, uses this.selectedAlerts & this.selectedBulkAlertAction
           );
       },
  
      // --- Helpers ---
      openDynamicChart(ticker) {
          const url = this.$detectEnvironment() + "/app/graficador-dinamico/ticker/" + ticker;
          window.open(url, "_blank");
      },
      getAlertTypeClass(key){ console.log('Alert type key:', key); const m={'not_eureker':'alert-type-not-eureker','support':'alert-type-support-changed','risk':'alert-type-risk-changed','entry_price_changed':'alert-type-entry-price-changed','possible_entry':'alert-type-possible-execution','stop_is_broken':'alert-type-stop-broken','max_amount_in_one_position':'alert-type-max-amount','stop':'alert-type-stop-broken','max_amount':'alert-type-max-amount','possible_execution':'alert-type-possible-execution'}; return m[key]||'alert-type-default'; },
      priceStep(decimals) { return (typeof decimals === 'number' && decimals >= 0) ? Math.pow(10, -decimals) : 0.01; },

      // --- Generic Confirmation Modal Methods ---
      openConfirmModal(title, message, action, data) {
        this.formError = null; // Clear general formError when opening confirmation
        this.executeFormError = null; // Clear specific modal errors too
        this.editFormError = null;
        this.bulkAlertError = null;
        this.confirmModalTitle = title;
        this.confirmModalMessage = message;
        this.confirmModalAction = action; // This will be the function to call
        this.confirmModalData = data;     // Data to pass to the action function
        this.showConfirmModal = true;
      },
      closeConfirmModal() {
        this.showConfirmModal = false;
        // Reset modal specific properties, but not this.formError, 
        // as it might be an error message from the executed action shown in the confirmation modal.
        // this.confirmModalTitle = ''; // Let them persist if modal is briefly hidden then reshown for error
        // this.confirmModalMessage = '';
        // this.confirmModalAction = null;
        // this.confirmModalData = null;
        if (this.isConfirmSubmitting) { // Only reset if it was actually submitting
            this.isConfirmSubmitting = false;
        }
      },
      async executeConfirmAction() {
        if (!this.confirmModalAction) return;
        
        // Use a local error var for the confirmation modal action itself, to distinguish from parent formError prop
        let confirmationActionError = null; 
        this.isConfirmSubmitting = true;
        try {
          await this.confirmModalAction(this.confirmModalData); // Call the stored action
          this.closeConfirmModal(); // Close confirm modal on success of the action
        } catch (err) {
          confirmationActionError = `Error en la acción: ${err.message || 'Ocurrió un error.'}`;
          console.error("Confirmation Action failed:", err);
          // Display this specific error. We could use this.formError if we ensure it's cleared correctly before other modals use it.
          this.formError = confirmationActionError; // Use the general formError for display in this modal
          // Keep the modal open to show the error
        } finally {
          this.isConfirmSubmitting = false;
        }
      },

      // --- Report Alert Error Modal Methods ---
      openReportErrorModal(alert) {
          this.alertToReportError = alert;
          this.reportErrorMessage = '';
          this.reportErrorFormError = null;
          this.showReportErrorModal = true;
      },
      closeReportErrorModal() {
          this.showReportErrorModal = false;
          this.alertToReportError = null;
          this.reportErrorMessage = '';
          this.reportErrorSubmitting = false;
      },
      async handleReportErrorConfirmed() {
          if (!this.alertToReportError) return;
          this.reportErrorSubmitting = true;
          this.reportErrorFormError = null;
          try {
              await apiService.createAlertState(this.portfolioId, {
                  alert_id: this.alertToReportError.id,
                  state: 'error_reported',
                  ticker: this.alertToReportError.ticker,
                  message: this.reportErrorMessage || 'Error reportado sin mensaje adicional.' // Use default if empty
              });
              await this.fetchAlerts();
              this.closeReportErrorModal();
          } catch (err) {
              this.reportErrorFormError = `Error reportando: ${err.message}`;
          } finally {
              this.reportErrorSubmitting = false;
          }
      },

      // --- Distance to Entry Formatter ---
      formatDistanceToEntry(pos) {
        // First try to use the backend provided entry_distance field
        if (typeof pos.entry_distance === 'number') {
          return this.formatPercentage(pos.entry_distance);
        }
        
        // Fallback: calculate distance to entry if current_price and in_price are available
        if (typeof pos.current_price === 'number' && typeof pos.in_price === 'number' && pos.current_price !== 0) {
          const distance = (Math.abs(pos.in_price - pos.current_price) / pos.current_price) * 100;
          return this.formatPercentage(distance);
        }
        
        // If distance_to_entry field exists (legacy support)
        if (typeof pos.distance_to_entry === 'number') {
          return this.formatPercentage(pos.distance_to_entry);
        }
        
        return 'N/A';
      },

      // --- Sorting Methods ---
      getSortValue(position, sortKey) {
        switch (sortKey) {
          case 'ticker':
            return position.ticker || '';
          case 'instrument_number':
            return position.instrument_number || 0;
          case 'risk':
            return position.risk || 0;
          case 'in_price':
            return position.in_price || 0;
          case 'out_price':
            return position.out_price || 0;
          case 'support_price':
            return position.support_price || 0;
          case 'support_date':
            return position.support_date ? parseDate(position.support_date) : new Date(0);
          case 'currency_value':
            return position.currency_value || 0;
          case 'entry_distance':
            // Use the same logic as formatDistanceToEntry but return numeric value
            if (typeof position.entry_distance === 'number') {
              return position.entry_distance;
            }
            if (typeof position.current_price === 'number' && typeof position.in_price === 'number' && position.current_price !== 0) {
              return (Math.abs(position.in_price - position.current_price) / position.current_price) * 100;
            }
            if (typeof position.distance_to_entry === 'number') {
              return position.distance_to_entry;
            }
            return 0;
          case 'notes':
            return position.notes || '';
          case 'company_name':
            return position.company_name || '';
          case 'direction':
            return position.direction || 0;
          case 'instrument_type':
            return position.instrument_type || '';
          case 'currency':
            return position.currency || '';
          case 'current_price':
            return position.current_price || 0;
          case 'amount':
            return position.amount || 0;
          case 'tthh_profit':
            return position.tthh_profit || 0;
          case 'wave_number':
            return position.wave_number || 0;
          case 'cfd_margin':
            return position.cfd_margin || 0;
          case 'is_ipo':
            return position.is_ipo || 0;
          default:
            return position[sortKey] || '';
        }
      },

      sortByColumn(column) {
        if (this.sortBy === column) {
          // If clicking the same column, toggle direction
          this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
        } else {
          // If clicking a different column, set it as the sort column with ascending direction
          this.sortBy = column;
          this.sortDirection = 'asc';
        }
      },

      getSortIconClass(column) {
        if (this.sortBy !== column) {
          return 'sort-icon sort-none';
        }
        return this.sortDirection === 'asc' ? 'sort-icon sort-asc' : 'sort-icon sort-desc';
      },

      // --- Edit Form Validation Methods ---
      markEditFieldTouched(fieldName) {
          this.editTouchedFields[fieldName] = true;
      },

      shouldShowEditError(fieldName) {
          return (this.editTouchedFields[fieldName] || this.editSubmitAttempted) && this[`edit${fieldName.charAt(0).toUpperCase() + fieldName.slice(1).replace('_', '')}Error`];
      },

      validateEditInPrice() {
          const inPrice = parseFloat(this.editFormData.in_price);
          
          if (this.editFormData.in_price === null || this.editFormData.in_price === undefined || 
              this.editFormData.in_price === '' || isNaN(inPrice)) {
              this.editInPriceError = 'El precio de entrada es requerido y debe ser un número válido.';
              this.editInPriceValid = false;
          } else if (inPrice <= 0) {
              this.editInPriceError = 'El precio de entrada debe ser mayor que 0.';
              this.editInPriceValid = false;
          } else if (inPrice > 1000000) {
              this.editInPriceError = 'El precio de entrada no puede exceder 1.000.000.';
              this.editInPriceValid = false;
          } else {
              this.editInPriceError = null;
              this.editInPriceValid = true;
          }
      },

      validateEditSupportPrice() {
          const supportPrice = parseFloat(this.editFormData.support_price);
          
          if (this.editFormData.support_price === null || this.editFormData.support_price === undefined || 
              this.editFormData.support_price === '' || isNaN(supportPrice)) {
              this.editSupportPriceError = 'El precio de soporte es requerido y debe ser un número válido.';
              this.editSupportPriceValid = false;
          } else if (supportPrice <= 0) {
              this.editSupportPriceError = 'El precio de soporte debe ser mayor que 0.';
              this.editSupportPriceValid = false;
          } else if (supportPrice > 1000000) {
              this.editSupportPriceError = 'El precio de soporte no puede exceder 1.000.000.';
              this.editSupportPriceValid = false;
          } else {
              this.editSupportPriceError = null;
              this.editSupportPriceValid = true;
          }
      },

      validateEditSupportDate() {
          if (!this.editFormData.support_date) {
              this.editSupportDateError = 'La fecha de soporte es requerida.';
              this.editSupportDateValid = false;
              return;
          }

          const parsedDate = parseDate(this.editFormData.support_date);
          if (!parsedDate) {
              this.editSupportDateError = 'La fecha ingresada no es válida.';
              this.editSupportDateValid = false;
              return;
          }

          // Check if date is not in the future
          const today = new Date();
          today.setHours(23, 59, 59, 999);
          if (parsedDate > today) {
              this.editSupportDateError = 'La fecha de soporte no puede ser futura.';
              this.editSupportDateValid = false;
              return;
          }

          this.editSupportDateError = null;
          this.editSupportDateValid = true;
      },

      formatEditDisplayValue(value) {
          if (value === null || value === undefined) return '';
          if (typeof value === 'number') {
              return value.toString();
          }
          return value;
      },

      handleEditFocus(event, modelKey) {
          if (this.editFormData[modelKey] !== null && this.editFormData[modelKey] !== undefined) {
              // Display raw value for editing
              event.target.value = this.editFormData[modelKey].toString();
          }
      },

      handleEditNumericInput(event, modelKey) {
          let value = event.target.value;
          // Only allow digits, dots, and commas (no dashes/minus signs)
          let sanitizedValue = value.replace(/[^\d.,]/g, '');
          
          // Handle decimal separators: find the last comma or dot as decimal separator
          const lastCommaIndex = sanitizedValue.lastIndexOf(',');
          const lastDotIndex = sanitizedValue.lastIndexOf('.');
          const lastDecimalIndex = Math.max(lastCommaIndex, lastDotIndex);
          
          if (lastDecimalIndex !== -1) {
              // Split into integer and decimal parts
              const integerPart = sanitizedValue.substring(0, lastDecimalIndex).replace(/[.,]/g, ''); // Remove all dots/commas from integer part
              const decimalPart = sanitizedValue.substring(lastDecimalIndex + 1);
              
              // Limit decimal part to appropriate number of digits
              const maxDecimals = modelKey.includes('price') && this.positionToEdit ? this.positionToEdit.stock_decimals || 2 : 2;
              const limitedDecimalPart = decimalPart.substring(0, maxDecimals);
              
              // Reconstruct with dot as decimal separator
              sanitizedValue = integerPart + '.' + limitedDecimalPart;
          }
          
          // Prevent starting with decimal point - require leading zero
          if (sanitizedValue.startsWith('.')) {
              sanitizedValue = '0' + sanitizedValue;
          }

          event.target.value = sanitizedValue;

          if (sanitizedValue === '') {
              this.editFormData[modelKey] = null;
          } else if (!isNaN(parseFloat(sanitizedValue)) && isFinite(sanitizedValue)) {
              this.editFormData[modelKey] = sanitizedValue;
          } else if (sanitizedValue.endsWith('.')) {
              this.editFormData[modelKey] = sanitizedValue;
          } else {
              this.editFormData[modelKey] = null;
          }

          // Real-time validation
          this.validateEditField(modelKey);
      },

      formatEditNumericOnBlur(event, modelKey) {
          let value = this.editFormData[modelKey];
          
          if (value === null || String(value).trim() === '') {
              this.editFormData[modelKey] = null;
              return;
          }
          
          if(typeof value === 'string' && value.endsWith('.')) {
              value = value.slice(0, -1);
          }

          const numericValue = parseFloat(value);
          if (!isNaN(numericValue) && isFinite(numericValue)) {
              // Round to appropriate decimal places
              const maxDecimals = modelKey.includes('price') && this.positionToEdit ? this.positionToEdit.stock_decimals || 2 : 2;
              const roundedValue = Math.round(numericValue * Math.pow(10, maxDecimals)) / Math.pow(10, maxDecimals);
              this.editFormData[modelKey] = roundedValue;
          } else {
              this.editFormData[modelKey] = null;
          }

          // Validate after formatting
          this.validateEditField(modelKey);
      },

      validateEditField(modelKey) {
          switch(modelKey) {
              case 'in_price':
                  this.validateEditInPrice();
                  break;
              case 'support_price':
                  this.validateEditSupportPrice();
                  break;
              case 'support_date':
                  this.validateEditSupportDate();
                  break;
          }
      },

      validateEditInitialValues() {
          if (this.editFormData.in_price !== null) this.validateEditInPrice();
          if (this.editFormData.support_price !== null) this.validateEditSupportPrice();
          if (this.editFormData.support_date) this.validateEditSupportDate();
      },

      // --- Column Customization Methods ---
      handleColumnsChanged(newColumns) {
          this.activeColumns = newColumns;
      },

      initializeColumns() {
          // Try to load saved configuration first
          const savedConfig = this.loadColumnConfiguration();
          
          if (savedConfig && savedConfig.length > 0) {
              this.activeColumns = this.mergeWithSavedConfig(savedConfig);
          } else {
              // Initialize active columns from defaultColumns if no saved config
              this.activeColumns = JSON.parse(JSON.stringify(this.defaultColumns));
          }
      },

      loadColumnConfiguration() {
          try {
              const saved = localStorage.getItem('tableColumns_valoresEnEspera');
              return saved ? JSON.parse(saved) : null;
          } catch (error) {
              console.warn('Error loading column configuration:', error);
              return null;
          }
      },

      mergeWithSavedConfig(savedConfig) {
          const merged = [];
          
          // First, add columns in the order they were saved
          savedConfig.forEach(savedCol => {
              const currentCol = this.defaultColumns.find(col => col.key === savedCol.key);
              if (currentCol) {
                  merged.push({
                      ...currentCol,
                      visible: savedCol.visible !== undefined ? savedCol.visible : currentCol.visible
                  });
              }
          });
          
          // Then, add any new columns from defaultColumns that weren't in the saved config
          this.defaultColumns.forEach(currentCol => {
              if (!merged.find(col => col.key === currentCol.key)) {
                  merged.push({ ...currentCol });
              }
          });
          
          return merged;
      },

      getCellClass(column) {
          const classes = [];
          if (column.numeric) classes.push('number-cell');
          if (column.key === 'ticker') classes.push('ticker-cell');
          if (column.key === 'notes') classes.push('notes-cell');
          return classes.join(' ');
      },

      renderCell(position, column) {
          switch (column.key) {
              case 'ticker':
                  return this.renderTickerCell(position);
              case 'instrument_number':
                  return this.formatNumber(position.instrument_number);
              case 'risk':
                  return this.formatPercentage(position.risk);
              case 'in_price':
                  return this.formatPrice(position.in_price, position.stock_decimals);
              case 'out_price':
                  return this.formatPrice(position.out_price, position.stock_decimals);
              case 'support_price':
                  return this.formatPrice(position.support_price, position.stock_decimals);
              case 'support_date':
                  return this.formatDate(position.support_date);
              case 'currency_value':
                  return position.currency_value ? this.formatNumberAnglo(position.currency_value, 4) : 'N/A';
              case 'entry_distance':
                  return this.formatDistanceToEntry(position);
              case 'notes':
                  return this.renderNotesCell(position);
              case 'current_price':
                  return `${this.formatPrice(position.current_price, position.stock_decimals)}${this.getCurrencySymbol(position.currency)}`;
              case 'amount':
                  return this.formatCurrency(position.amount, this.portfolioCurrency);
              case 'tthh_profit':
                  return this.formatPercentage(position.tthh_profit);
              case 'wave_number':
                  return position.wave_number !== null && position.wave_number !== undefined ? position.wave_number : '-';
              // case 'cfd_margin':
              //     return position.instrument_type === 'CFD' ? (position.cfd_margin !== null && position.cfd_margin !== undefined ? this.formatPrice(position.cfd_margin, 2) : '-') : '-';
              // case 'is_ipo':
              //     return position.is_ipo == 1 ? 'Sí' : 'No';
              default:
                  return position[column.key] || '-';
          }
      },

      renderTickerCell(position) {
          const tooltip = this.generateTickerTooltip(position);
          return `
              <div class="ticker-content-wrapper" title="${tooltip}">
                  <div class="ticker-left">
                      <b>${position.ticker}</b>
                      <small>${position.company_name}</small>
                  </div>
                  <div class="ticker-right">
                      <span class="direction-icon ${position.direction == 1 ? 'bullish' : 'bearish'} material-icons-outlined" title="${position.direction == 1 ? 'Alcista' : 'Bajista'}">${position.direction == 1 ? 'trending_up' : 'trending_down'}</span>
                      <span class="instrument-icon material-icons-outlined" title="${position.instrument_type === 'STOCK' ? 'Acción' : 'CFD'}">${position.instrument_type === 'STOCK' ? 'show_chart' : 'candlestick_chart'}</span>
                      <small class="currency">${position.currency || '-'}</small>
                  </div>
              </div>
          `;
      },

      renderNotesCell(position) {
          if (!position.editingNotes) {
              return `
                  <span>${position.notes || '-'}</span>
                  <button class="btnGcn btnGcn-link btnGcn-sm" onclick="this.closest('tr').querySelector('td').dispatchEvent(new CustomEvent('start-edit-notes', { detail: { id: '${position.id}' } }))" title="Editar Notas">
                      <span class="material-icons-outlined">edit</span>
                  </button>
              `;
          } else {
              return `
                  <textarea class="form-control-gcn form-control-gcn-sm" data-id="${position.id}">${position.editNotesValue}</textarea>
                  <button class="btnGcn btnGcn-link btnGcn-sm" onclick="this.closest('tr').querySelector('td').dispatchEvent(new CustomEvent('save-notes', { detail: { id: '${position.id}' } }))" title="Guardar Notas">
                      <span class="material-icons-outlined">save</span>
                  </button>
              `;
          }
      },
    },
    created() {
      this.initializeColumns();
    },
    watch: {
      portfolioId: {
        immediate: true,
        handler(newId){
          if(newId){
            this.fetchData();
          } else {
            this.waitingPositions=[];
            this.alerts=[];
            this.error=null;
            this.alertsError=null;
            this.selectedPositions=[];
            this.selectedAlerts=[];
            this.alertGroupCollapsedState={};
          }
        },
      },
    },
  };
  </script>
  
  <style scoped>
  /* --- Colors from App Theme --- */
  .valores-espera-container {
    color: var(--color-text-primary);
    font-size: 0.92rem;
    padding: 0 10px;
  }

  .material-icons-outlined {
    font-size: 16px;
    line-height: 1;
    vertical-align: middle;
    margin-right: 3px;
  }
  
  .btnGcn-sm .material-icons-outlined {
    font-size: 16px;
    margin-right: 0;
  }

  /* --- Custom Button Styles --- */
  .custom-btnGcn {
    background-color: var(--color-highlight);
    color: var(--color-text-on-highlight);
    border: 1px solid var(--color-highlight);
    transition: all 0.2s ease;
    padding: 6px 12px;
    font-size: 0.85rem;
    cursor: pointer;
  }

  .custom-btnGcn:hover {
    background-color: var(--color-text-primary);
    color: var(--color-background);
    border-color: var(--color-text-primary);
    transform: translateY(-1px);
    box-shadow: 0 2px 8px var(--color-outline);
  }

  .custom-btnGcn:active {
    transform: translateY(0);
    box-shadow: 0 1px 4px var(--color-outline);
  }

  .custom-btnGcn:focus {
    outline: none;
    box-shadow: 0 0 0 3px var(--color-active-surface);
  }

  .custom-btnGcn-outline {
    background-color: transparent;
    color: var(--color-highlight);
    border: 1px solid var(--color-outline);
    padding: 6px 12px;
    font-size: 0.85rem;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .custom-btnGcn-outline:hover {
    background-color: var(--color-surface);
    color: var(--color-text-primary);
    border-color: var(--color-highlight);
  }

  .custom-btnGcn-outline:active {
    background-color: var(--color-active-surface);
  }

  .custom-btnGcn-outline:focus {
    outline: none;
    box-shadow: 0 0 0 3px var(--color-active-surface);
  }

  /* --- Alerts Styling --- */
  .alerts-section { 
    background-color: var(--color-surface); 
    border: none; 
    border-radius: 6px;
    padding: 12px; 
    margin-bottom: 20px; 
  }
  
  .alerts-header { 
    display: flex; 
    justify-content: space-between; 
    align-items: center; 
    margin-bottom: 12px; 
    padding-bottom: 8px; 
    border-bottom: 1px solid var(--color-outline); 
    flex-wrap: wrap; 
    gap: 8px; 
  }
  
  .alerts-header h3 { 
    margin: 0; 
    font-size: 1.15em; 
    color: var(--color-text-primary); 
    border-bottom: none; 
    padding-bottom: 0; 
    text-shadow: 0 1px 2px var(--color-outline);
  }
  
  .alert-group { 
    border: 1px solid var(--color-outline); 
    border-radius: 5px; 
    margin-bottom: 8px; 
    overflow: hidden; 
    background-color: var(--color-surface); 
    box-shadow: 0 2px 4px var(--color-outline);
    transition: all 0.2s ease; 
  }
  
  .alert-group:hover {
    box-shadow: 0 3px 8px var(--color-outline);
    transform: translateY(-1px);
  }
  
  .alert-group.is-collapsed { 
    background-color: var(--color-background); 
  }
  
  .alert-group h4.collapsible-header { 
    margin: 0; 
    padding: 8px 12px; 
    font-size: 1em; 
    font-weight: 600; 
    background-color: var(--color-surface); 
    border-bottom: 1px solid var(--color-outline); 
    display: flex; 
    justify-content: space-between; 
    align-items: center; 
    cursor: pointer; 
    transition: background-color 0.2s ease; 
    color: var(--color-text-primary);
  }
  
  .alert-group.is-collapsed h4.collapsible-header { 
    border-bottom-color: transparent; 
  }
  
  .alert-group h4.collapsible-header:hover { 
    background-color: var(--color-active-surface); 
  }
  
  .collapse-icon { 
    font-family: monospace; 
    margin-right: 8px; 
    width: 1em; 
    display: inline-block; 
    text-align: center; 
    color: var(--color-text-secondary); 
  }
  
  .alert-group-icon { 
    margin-right: 8px; 
    color: var(--color-highlight);
  }
  
  /* Specific Alert Group Styling */
  .alert-group.alert-type-not-eureker { border-color: var(--color-warning); } 
  .alert-group.alert-type-not-eureker h4 { 
    background: linear-gradient(to right, var(--color-active-surface), var(--color-surface));
    color: var(--color-warning); 
  } 
  
  .alert-group.alert-type-support-changed { border-color: var(--color-warning); } 
  .alert-group.alert-type-support-changed h4 { 
    background: linear-gradient(to right, var(--color-active-surface), var(--color-surface));
    color: var(--color-warning); 
  } 
  
  .alert-group.alert-type-risk-changed { border-color: var(--color-error); } 
  .alert-group.alert-type-risk-changed h4 { 
    background: linear-gradient(to right, var(--color-active-surface), var(--color-surface));
    color: var(--color-error); 
  } 
  
  .alert-group.alert-type-stop-broken { border-color: var(--color-error); } 
  .alert-group.alert-type-stop-broken h4 { 
    background: linear-gradient(to right, var(--color-active-surface), var(--color-surface));
    color: var(--color-error); 
  } 
  
  .alert-group.alert-type-max-amount { border-color: var(--color-error); } 
  .alert-group.alert-type-max-amount h4 { 
    background: linear-gradient(to right, var(--color-active-surface), var(--color-surface));
    color: var(--color-error); 
  } 
  
  .alert-group.alert-type-entry-price-changed { border-color: var(--color-warning); } 
  .alert-group.alert-type-entry-price-changed h4 { 
    background: linear-gradient(to right, var(--color-active-surface), var(--color-surface));
    color: var(--color-warning); 
  } 
  
  .alert-group.alert-type-possible-execution { border-color: var(--color-warning); } 
  .alert-group.alert-type-possible-execution h4 { 
    background: linear-gradient(to right, var(--color-active-surface), var(--color-surface));
    color: var(--color-warning); 
  } 
  
  .alert-group.alert-type-default { border-color: var(--color-outline); } 
  .alert-group.alert-type-default h4 { 
    background: linear-gradient(to right, var(--color-active-surface), var(--color-surface));
    color: var(--color-text-primary); 
  } 
  
  .alert-group-content { 
    padding: 0; 
    max-height: 300px; /* Limit height for alert tables */
    overflow-y: auto; /* Enable vertical scrolling */
  }
  
  .alert-table { 
    width: 100%; 
    border-collapse: collapse; 
    font-size: 0.85em;
  }
  
  .alert-table th, .alert-table td { 
    padding: 6px 10px; 
    text-align: left; 
    border-bottom: 1px solid var(--color-outline); 
    vertical-align: middle; 
    color: var(--color-text-primary);
  }
  
  .alert-table thead th { 
    background-color: var(--color-surface); 
    font-weight: 600; 
    color: var(--color-text-secondary); 
    border-bottom-width: 2px; 
    font-size: 0.9em;
    padding: 6px 10px;
    position: sticky; /* Make header sticky */
    top: 0; /* Stick to top when scrolling */
    z-index: 10; /* Ensure header stays above content */
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1); /* Add shadow for better visibility */
    border-bottom: 2px solid var(--color-outline); /* Stronger border for sticky header */
  }
  
  .alert-table tbody tr:last-child td { 
    border-bottom: none; 
  }
  
  .alert-table td.alert-actions { 
    white-space: nowrap; 
    text-align: right; 
  }
  
  .alert-table .btnGcn { 
    margin-left: 4px; 
    padding: 2px 8px;
    font-size: 0.85em;
  }
  
  /* --- Waiting List & Table Styling --- */
  .list-actions { 
    display: flex; 
    justify-content: space-between; 
    align-items: center; 
    margin-bottom: 15px; 
    flex-wrap: wrap; 
    gap: 8px; 
    padding: 8px 12px;
    border-radius: 5px;
    background: linear-gradient(to right, var(--color-active-surface), var(--color-background));
  }

  .list-actions-right {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }
  
  .bulk-actions { 
    display: flex; 
    align-items: center; 
    gap: 5px; 
  }
  
  .bulk-actions select { 
    padding: 3px 6px; 
    font-size: 0.85em; 
    height: auto; 
    border-radius: 4px; 
    border: 1px solid var(--color-outline);
    background-color: var(--color-surface);
    color: var(--color-text-primary);
  }
  
  .table-responsive { 
    overflow-x: auto; 
    margin-top: 12px; 
    border-radius: 4px;
    box-shadow: 0 2px 8px var(--color-outline);
    margin-bottom: 20px;
    max-height: 70vh; /* Limit height to enable scrolling */
    overflow-y: auto; /* Enable vertical scrolling */
  }
  
  .table.data-table { 
    width: 100%; 
    border-collapse: collapse; 
    font-size: 0.85em; 
  }
  
  .data-table th, .data-table td { 
    border: 1px solid var(--color-outline); 
    padding: 6px 8px; 
    text-align: center; /* Center alignment for all cells */
    vertical-align: middle; 
    color: var(--color-text-primary);
    line-height: 1.3;
  }
  
  .data-table th { 
    background-color: var(--color-surface); 
    font-weight: 600; 
    white-space: nowrap; 
    color: var(--color-text-secondary);
    text-align: center; /* Center align header text */
    font-size: 0.9em;
    padding: 6px 8px;
    position: sticky; /* Make header sticky */
    top: 0; /* Stick to top when scrolling */
    z-index: 10; /* Ensure header stays above content */
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1); /* Add shadow for better visibility */
    border-bottom: 2px solid var(--color-outline); /* Stronger border for sticky header */
  }
  
  /* Removed alternating row colors per user request */
  /* .data-table tbody tr:nth-child(even) { 
    background-color: var(--color-active-surface); 
  } */
  
  .data-table tbody tr:hover { 
    background-color: var(--color-active-surface); 
  }
  
  .direction-icon, .instrument-icon { 
    font-size: 1.1em; 
    display: inline-block; 
    text-align: center; 
    width: 18px; 
  }
  
  .direction-icon.bullish { 
    color: var(--color-success); 
    text-shadow: 0 0 6px var(--color-success);
  }
  
  .direction-icon.bearish { 
    color: var(--color-error); 
    text-shadow: 0 0 6px var(--color-error);
  }
  
  .ticker-cell { 
    font-weight: 600; 
    text-align: left; /* Keep ticker column left-aligned */
  }
  
  /* Override centering specifically for ticker cells in table body */
  .data-table tbody .ticker-cell {
    text-align: left;
  }
  
  .ticker-cell small { 
    color: var(--color-text-secondary); 
    font-size: 0.85em; 
    display: block; 
    font-weight: 400;
    line-height: 1.2;
  }
  
  .ticker-cell small.entry-type {
    font-style: italic;
    font-size: 0.8em;
    margin-top: 2px;
  }
  
  .ticker-cell .instrument-icon {
    margin-right: 4px; /* Space between icon and ticker */
    vertical-align: baseline;
  }
  
  .ticker-extra-details {
    margin-top: 4px;
    font-size: 0.9em; /* Slightly smaller */
    line-height: 1.4;
    color: var(--color-text-secondary);
  }
  
  .ticker-extra-details small {
    display: block; /* Each item on a new line */
    margin-bottom: 1px;
  }
  
  .ticker-extra-details small b {
    color: var(--color-text-secondary); /* Keep labels subtle */
    font-weight: 500;
    min-width: 60px; /* Align labels */
    display: inline-block;
  }
  
  .notes-cell { 
    min-width: 150px; 
    white-space: pre-wrap; 
    font-size: 0.9em;
  }
  
  .notes-cell textarea { 
    width: 100%; 
    min-height: 50px; 
    font-size: 0.9em; 
    padding: 4px; 
    box-sizing: border-box; 
    border-radius: 3px; 
    border: 1px solid var(--color-outline);
    background-color: var(--color-surface);
    color: var(--color-text-primary);
  }
  
  .notes-cell .btnGcn-link { 
    vertical-align: top; 
    color: var(--color-text-link);
  }
  
  .options-cell { 
    white-space: nowrap; 
    text-align: center; 
  }
  
  .options-cell .btnGcn-link { 
    padding: 0; 
    width: 24px;
    height: 24px;
    font-size: 16px; 
    text-decoration: none; 
    vertical-align: middle; 
    color: var(--color-text-secondary); 
    background-color: transparent;
    border: none;
    border-radius: 4px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin: 0 1px;
    cursor: pointer;
    transition: all 0.15s ease;
  }
  
  .options-cell .btnGcn-link:hover { 
    color: var(--color-text-primary); 
    background-color: var(--color-surface);
    transform: scale(1.1);
  }

  .options-cell .btnGcn-link:active {
    transform: scale(1.05);
    background-color: var(--color-active-surface);
  }

  .options-cell .btnGcn-link:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-active-surface);
  }
  
  .options-cell .btnGcn-link.btnGcn-success { 
    color: var(--color-success); 
  } 

  .options-cell .btnGcn-link.btnGcn-success:hover {
    color: var(--color-text-on-highlight);
    background-color: var(--color-success);
  }
  
  .options-cell .btnGcn-link.btnGcn-danger { 
    color: var(--color-error); 
  }

  .options-cell .btnGcn-link.btnGcn-danger:hover {
    color: var(--color-text-on-highlight);
    background-color: var(--color-error);
  }

  .options-cell .btnGcn-link.btnGcn-graph {
    color: var(--color-highlight);
  }

  .options-cell .btnGcn-link.btnGcn-graph:hover {
    color: var(--color-text-on-highlight);
    background-color: var(--color-highlight);
  }

  .error-message-gcn {
    color: var(--color-error);
    padding: 8px 12px;
    border-radius: 4px;
    margin-bottom: 12px;
    background-color: var(--color-active-surface);
    font-size: 0.9em;
  }

  .loading-indicator-gcn {
    color: var(--color-text-secondary);
    text-align: center;
    padding: 15px;
    font-size: 0.9em;
  }

  /* Form styling */
  .form-control-gcn {
    background-color: var(--color-surface);
    border: 1px solid var(--color-outline);
    color: var(--color-text-primary);
    padding: 6px 10px;
    border-radius: 4px;
    font-size: 0.9em;
  }

  .form-control-gcn:focus {
    border-color: var(--color-highlight);
    outline: none;
    box-shadow: 0 0 0 2px var(--color-active-surface);
  }

  .btnGcn-secondary {
    background-color: var(--color-surface);
    color: var(--color-text-primary);
    border: 1px solid var(--color-outline);
    font-size: 0.85em;
    padding: 4px 10px;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .btnGcn-secondary:hover {
    background-color: var(--color-active-surface);
    border-color: var(--color-text-secondary);
  }

  .btnGcn-secondary:active {
    background-color: var(--color-outline);
  }

  .btnGcn-secondary:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-active-surface);
  }

  .btnGcn-warning {
    background-color: var(--color-warning);
    color: var(--color-text-primary);
    border: 1px solid var(--color-warning);
    font-size: 0.85em;
    padding: 4px 10px;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .btnGcn-warning:hover {
    background-color: var(--color-text-primary);
    color: var(--color-background);
    border-color: var(--color-text-primary);
  }

  .btnGcn-warning:active {
    opacity: 0.9;
  }

  .btnGcn-warning:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-active-surface);
  }

  .btnGcn-success {
    background-color: var(--color-success);
    color: var(--color-text-on-highlight);
    border: 1px solid var(--color-success);
    font-size: 0.85em;
    padding: 4px 10px;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .btnGcn-success:hover {
    background-color: var(--color-text-primary);
    color: var(--color-background);
    border-color: var(--color-text-primary);
    transform: translateY(-1px);
  }

  .btnGcn-success:active {
    transform: translateY(0);
    opacity: 0.9;
  }

  .btnGcn-success:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-active-surface);
  }

  .btnGcn-danger {
    background-color: var(--color-error);
    color: var(--color-text-on-highlight);
    border: 1px solid var(--color-error);
    font-size: 0.85em;
    padding: 4px 10px;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .btnGcn-danger:hover {
    background-color: var(--color-text-primary);
    color: var(--color-background);
    border-color: var(--color-text-primary);
    transform: translateY(-1px);
  }

  .btnGcn-danger:active {
    transform: translateY(0);
    opacity: 0.9;
  }

  .btnGcn-danger:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-active-surface);
  }

  /* Style for disabled icons in options-cell */
  .options-cell .btnGcn-link.disabled {
    color: var(--color-text-inactive);
    cursor: not-allowed;
    pointer-events: none;
    opacity: 0.3;
    transition: none;
  }

  /* Completely disable hover effects for disabled buttons */
  .options-cell .btnGcn-link.disabled:hover,
  .options-cell .btnGcn-link.disabled:focus,
  .options-cell .btnGcn-link.disabled:active {
    background-color: transparent !important;
    color: var(--color-text-inactive) !important;
    transform: none !important;
    box-shadow: none !important;
    opacity: 0.3 !important;
  }

  /* Disabled state for all button types */
  .custom-btnGcn:disabled,
  .custom-btnGcn-outline:disabled,
  .btnGcn-secondary:disabled,
  .btnGcn-warning:disabled,
  .btnGcn-success:disabled,
  .btnGcn-danger:disabled {
    background-color: var(--color-surface);
    color: var(--color-text-inactive);
    border-color: var(--color-outline);
    cursor: not-allowed;
    opacity: 0.5;
    transform: none !important;
    box-shadow: none !important;
    pointer-events: none;
  }

  .data-table td.number-cell {
    text-align: center; /* Center align numeric data */
  }

  /* NEW Ticker Cell Layout Styles */
  .ticker-cell {
    vertical-align: top; /* Align content to the top if needed */
  }

  .ticker-content-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center; /* Changed from flex-start to center */
    width: 100%;
  }

  .ticker-left {
    flex-grow: 1; /* Allow left side to take up available space */
    margin-right: 8px; /* Add some space between left and right */
  }
  .ticker-left b {
    display: block; /* Ensure ticker is on its own line */
  }
  .ticker-left small {
    display: block; /* Ensure company name is below ticker */
  }

  .current-price-info {
    display: block;
    color: var(--color-text-secondary);
    font-weight: 400;
    font-size: 0.8em;
    margin-top: 2px;
  }

  .ticker-right {
    display: flex;
    flex-direction: column; /* Stack items vertically */
    align-items: flex-end; /* Align items to the right */
    flex-shrink: 0; /* Prevent right side from shrinking */
    text-align: right;
  }

  .ticker-right .direction-icon,
  .ticker-right .instrument-icon {
    display: block; /* Ensure icons are on separate lines */
    margin-bottom: 1px; /* Small space between icons/currency */
    width: auto; /* Override fixed width if needed */
    text-align: right;
  }
  .ticker-right .instrument-icon {
    font-size: 1em; /* Adjust icon size if needed */
  }
  .ticker-right small.currency {
    font-size: 0.8em;
    color: var(--color-text-secondary);
    margin-top: 2px;
  }
  /* END NEW Ticker Cell Layout Styles */

  /* Style inputs within modals */
  .modal-body-gcn .form-group-gcn {
      margin-bottom: 15px;
  }
  .modal-body-gcn label {
      display: block;
      margin-bottom: 5px;
      font-weight: 500;
      font-size: 0.9em;
      color: var(--color-text-secondary);
  }

  /* REMOVE CONFLICTING STYLES - use global styles.css instead */
  .modal-body-gcn .form-control-gcn {
      /* Use global form styles - match styles.css */
      display: block;
      width: 100%;
      padding: 12px 14px;
      font-size: 1em;
      line-height: 1.5;
      color: var(--color-text-primary);
      background-color: var(--color-background);
      background-clip: padding-box;
      border: 1px solid var(--color-outline);
      border-radius: 8px;
      transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
      box-sizing: border-box;
  }

  .modal-body-gcn .form-control-gcn:focus {
    color: var(--color-text-primary);
    background-color: var(--color-background);
    border-color: var(--color-highlight);
    outline: 0;
    box-shadow: 0 0 0 0.2rem rgba(0, 173, 217, 0.25);
  }

  .modal-body-gcn small {
      font-size: 0.8em;
      color: var(--color-text-secondary);
      margin-top: 4px;
      display: block;
  }

  /* Form validation styles for edit modal (copied from PortfolioForm.vue) */
  .required {
    color: var(--color-error);
    font-weight: bold;
  }

  .error-message-gcn {
    color: var(--color-error);
    font-size: 0.8em;
    display: block;
    margin-top: 4px;
  }

  .text-primary {
    color: var(--color-text-primary);
  }

  .text-secondary {
    color: var(--color-text-secondary);
  }

  .form-control-gcn.error,
  .modal-body-gcn .form-control-gcn.error {
    border-color: var(--color-error) !important;
    box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25) !important;
  }

  .input-feedback {
    margin-top: 4px;
  }

  .character-count {
    float: right;
    font-size: 0.75em;
  }

  .form-status {
    padding: 10px;
    border-radius: 4px;
    margin-top: 15px;
    font-size: 0.9em;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .form-status.success {
    background-color: #d4edda;
    border: 1px solid #c3e6cb;
    color: #155724;
  }

  .form-status.error {
    background-color: #f8d7da;
    border: 1px solid #f5c6cb;
    color: #721c24;
  }

  .status-icon {
    font-weight: bold;
    font-size: 1.1em;
  }

  /* Additional form styling for better visual hierarchy */
  .modal-body-gcn label.text-primary {
    color: var(--color-text-primary) !important;
    font-weight: 600 !important;
  }

  .modal-body-gcn .required {
    margin-left: 2px !important;
  }



  /* Small screen adjustments */
  @media (max-width: 768px) {
    .data-table th, .data-table td {
      padding: 4px 6px;
      font-size: 0.85em;
    }
    
    .options-cell .btnGcn-link {
      width: 22px;
      height: 22px;
      font-size: 14px;
    }
    
    /* Adjust sticky header behavior on mobile */
    .data-table th {
      position: sticky;
      top: 0;
      z-index: 10;
    }
    
    .alert-table thead th {
      position: sticky;
      top: 0;
      z-index: 10;
    }
    
    /* Reduce max height on mobile for better usability */
    .table-responsive {
      max-height: 60vh;
    }
    
    .alert-group-content {
      max-height: 250px;
    }
  }

  .alert-group-bulk-actions select {
    padding: 2px 4px;
    font-size: 0.8em;
    height: auto;
    border-radius: 3px;
    border: 1px solid var(--color-outline);
    background-color: var(--color-surface);
    color: var(--color-text-secondary);
    width: 110px;
    text-overflow: ellipsis;
  }

  .alert-group-bulk-actions .btnGcn-sm {
  }

  /* Sortable Header Styles */
  .sortable-header {
    cursor: pointer;
    user-select: none;
    position: relative;
    transition: background-color 0.2s ease;
  }

  .sortable-header:hover {
    background-color: var(--color-active-surface) !important;
  }

  .sortable-header:active {
    background-color: var(--color-outline) !important;
  }

  .sort-icon {
    margin-left: 6px;
    font-size: 0.75em;
    opacity: 0.3;
    transition: opacity 0.2s ease;
    font-weight: normal;
    color: var(--color-text-secondary);
    vertical-align: baseline;
    display: inline-block;
    line-height: 1;
    position: relative;
    top: -1px;
  }

  .sortable-header:hover .sort-icon {
    opacity: 0.6;
  }

  .sort-icon.sort-asc {
    opacity: 1;
    color: var(--color-highlight);
    font-weight: 600;
  }

  .sort-icon.sort-asc::before {
    content: '▲';
  }

  .sort-icon.sort-desc {
    opacity: 1;
    color: var(--color-highlight);
    font-weight: 600;
  }

  .sort-icon.sort-desc::before {
    content: '▼';
  }

  .sort-icon.sort-none::before {
    content: '◆';
  }

  /* Exchange Rate Styles */
  .exchange-rate-btn {
    margin-top: 8px;
    white-space: nowrap;
    font-size: 0.8rem;
    padding: 6px 12px;
    transition: all 0.2s ease;
    align-self: flex-start;
  }

  .exchange-rate-btn:hover:not(:disabled) {
    background-color: var(--color-highlight);
    color: var(--color-text-on-highlight);
    border-color: var(--color-highlight);
  }

  .exchange-rate-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .exchange-rate-success {
    color: var(--color-success) !important;
    font-weight: 500;
  }

  @keyframes pulse {
    0% { opacity: 1; }
    50% { opacity: 0.6; }
    100% { opacity: 1; }
  }

  /* Edit Mode Selector Styles */
  .edit-mode-selector {
    margin-bottom: 20px;
    margin-top: 20px;
    padding: 16px;
    background-color: var(--color-surface);
    border-radius: 8px;
    border: 1px solid var(--color-outline);
  }

  .edit-mode-title {
    margin: 0 0 12px 0;
    font-size: 0.95em;
    font-weight: 600;
    color: var(--color-text-primary);
  }

  .edit-mode-options {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
  }

  .edit-mode-option {
    flex: 1;
    min-width: 180px;
    cursor: pointer;
    padding: 12px;
    border: 2px solid var(--color-outline);
    border-radius: 6px;
    background-color: var(--color-background);
    transition: all 0.2s ease;
    position: relative;
  }

  .edit-mode-option:hover {
    border-color: var(--color-highlight);
    background-color: var(--color-active-surface);
  }

  .edit-mode-option.active {
    border-color: var(--color-highlight);
    background-color: var(--color-active-surface);
    box-shadow: 0 0 0 1px var(--color-highlight);
  }

  .edit-mode-option input[type="radio"] {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }

  .mode-content {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .mode-icon {
    font-size: 1.2em;
    line-height: 1;
  }

  .mode-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .mode-text strong {
    font-size: 0.9em;
    color: var(--color-text-primary);
    font-weight: 600;
  }

  .mode-text small {
    font-size: 0.8em;
    color: var(--color-text-secondary);
    line-height: 1.2;
  }
  </style>